import dayjs from 'dayjs'

import { configPublic } from '@src/config.public'
import type { Badge } from '@src/typings/badge'
import {
  MY_PROGRESS_IN_PROGRESS_VARIANT,
  MY_PROGRESS_LIMITED_EDITION_VARIANT,
  MY_PROGRESS_RECENTLY_EARNED_VARIANT,
} from '@src/utils/my-progress.util'

export type BadgeCopy = {
  count: number
  currentCount: number
  ctaText: string | null
  hasProgressBar: boolean
  hideList: boolean
  messageText: string | null
  hasNextBadge: boolean
  specialText: string | null
  subtitle: string | null
}

type BadgeEarnedCount = Pick<GamificationApi.Badge, 'earnedCount' | 'status'>

function isStatusActive(status: GamificationApi.BadgeStatus) {
  return status === 'active'
}

export function isStatusCompleted(status: GamificationApi.BadgeStatus) {
  return status === 'completed'
}

function isStatusAvailable(status: GamificationApi.BadgeStatus) {
  return status === 'available'
}

function isTypeAggregate(type: GamificationApi.BadgeType) {
  return type === 'aggregate'
}

function isTypeCurated(type: GamificationApi.BadgeType) {
  return type === 'curated'
}

function isTypeTrack(type: GamificationApi.BadgeType) {
  return type === 'track'
}

export function isTrackBadge(
  badge: Pick<GamificationApi.Badge, 'type'>
): badge is GamificationApi.Badge & { type: 'track' | 'segment' } {
  return badge.type === 'track' || badge.type === 'segment'
}

export function hasUnexpiredLimitedEditionWindow(badge: GamificationApi.Badge): boolean {
  if (!badge.launchDate || !badge.startDate || !badge.endDate) return false

  return new Date(badge.endDate).getTime() >= Date.now()
}

export function orderLimitedEditionBadges(
  badges: GamificationApi.Badge[]
): GamificationApi.Badge[] {
  const now = Date.now()
  const completed = badges.filter((b) => b.status === 'completed')
  const active = badges.filter((b) => b.status === 'active')
  const availableStarted = badges.filter(
    (b) => b.status === 'available' && b.startDate != null && new Date(b.startDate).getTime() <= now
  )
  const announced = badges.filter(
    (b) =>
      b.status === 'available' && (b.startDate == null || new Date(b.startDate).getTime() > now)
  )
  return [...completed, ...active, ...availableStarted, ...announced]
}

export function shouldShowEarnedRibbon(
  badge: BadgeEarnedCount
): badge is GamificationApi.Badge & { earnedCount: number } {
  const earnedCount = badge.earnedCount ?? 0
  const hasEarnedMoreThanOnce = earnedCount > 1
  const isSecondTimeProgress =
    earnedCount === 1 && (isStatusActive(badge.status) || isStatusAvailable(badge.status))

  return hasEarnedMoreThanOnce || isSecondTimeProgress
}

export function getEarnedRibbonDisplayCount(badge: BadgeEarnedCount): number | null {
  if (!shouldShowEarnedRibbon(badge)) {
    return null
  }

  const earnedCount = badge.earnedCount ?? 0

  if (isStatusActive(badge.status) || isStatusAvailable(badge.status)) {
    return earnedCount + 1
  }

  return earnedCount
}

export function shouldShowPrimaryBadgeModal({
  badge,
  hasContent,
  isEnrolled,
}: {
  badge: Badge | null | undefined
  hasContent: boolean
  isEnrolled: boolean
}) {
  if (!hasContent || isEnrolled || !badge) return false
  if (badge.earnedCount <= 0) return false
  if (!badge.badgeItems?.length) return false
  return badge.remainingItems?.length === badge.badgeItems.length
}

const BASE_BADGE_TYPES: readonly GamificationApi.BadgeType[] = ['aggregate', 'curated']
const TRACK_BADGE_TYPES: readonly GamificationApi.BadgeType[] = ['track', 'segment']

/** Badge types surfaced on the badges page and celebration polling. */
export function getAllowedBadgeTypes(isTrackEnabled: boolean): GamificationApi.BadgeType[] {
  return isTrackEnabled ? [...BASE_BADGE_TYPES, ...TRACK_BADGE_TYPES] : [...BASE_BADGE_TYPES]
}

/** Needs cocoa gamification: only the v2 list carries the badge date window. */
export function isLimitedEditionBadgesEnabled(
  variant: string = configPublic.myProgressModuleAdditional
): boolean {
  return (
    variant === MY_PROGRESS_LIMITED_EDITION_VARIANT &&
    configPublic.featureFlags.isCocoaGamificationEnabled
  )
}

export function isRecentlyEarnedBadgesEnabled(
  variant: string = configPublic.myProgressModuleAdditional
): boolean {
  return variant === MY_PROGRESS_RECENTLY_EARNED_VARIANT
}

/** Needs cocoa gamification: primaryBadge is only populated on the v2 list. */
export function isInProgressBadgesEnabled(
  variant: string = configPublic.myProgressModuleAdditional
): boolean {
  return (
    variant === MY_PROGRESS_IN_PROGRESS_VARIANT &&
    configPublic.featureFlags.isCocoaGamificationEnabled
  )
}

// Program-level track badges qualify like curated ones; segment sub-badges do not.
function isInProgressCandidate(badge: GamificationApi.Badge): boolean {
  return (
    isStatusActive(badge.status) &&
    (isTypeCurated(badge.type) || isTypeTrack(badge.type)) &&
    badge.primaryBadge === true &&
    (badge.count ?? 0) > 0 &&
    (badge.currentCount ?? 0) > 0
  )
}

// The API orders badges by last update without sending the date, so the first one is newest.
// One workout can progress a program's legacy and track badges at once; the same-program track wins.
export function getInProgressBadge(
  badges: GamificationApi.Badge[] = []
): GamificationApi.Badge | null {
  const candidates = badges.filter(isInProgressCandidate)
  const mostRecent = candidates[0]

  if (!mostRecent || isTypeTrack(mostRecent.type)) return mostRecent ?? null

  const enrolledTrack = candidates.find(
    (badge) =>
      isTypeTrack(badge.type) && badge.entitySlug && badge.entitySlug === mostRecent.entitySlug
  )

  return enrolledTrack ?? mostRecent
}

const RECENTLY_EARNED_LIMIT = 4

function getCompletedTime(badge: GamificationApi.Badge): number {
  const time = badge.completedDate ? new Date(badge.completedDate).getTime() : NaN

  return Number.isNaN(time) ? -Infinity : time
}

// Undated badges sort last rather than being dropped; the API omits completedDate for some milestones.
export function getRecentlyEarnedBadges(
  badges: GamificationApi.Badge[] = [],
  limit: number = RECENTLY_EARNED_LIMIT
): GamificationApi.Badge[] {
  return badges
    .filter((badge) => isStatusCompleted(badge.status))
    .sort((a, b) => getCompletedTime(b) - getCompletedTime(a))
    .slice(0, limit)
}

/** Completed badges the user has not yet been shown a celebration modal for. */
export function getUncelebratedBadges(badges: GamificationApi.Badge[] = []) {
  return badges.filter((badge) => badge.id && badge.status === 'completed' && !badge.celebrated)
}

export function hasBadgeShareContent(badge: GamificationApi.Badge | null | undefined) {
  const content = badge?.content
  if (!content) return false

  return Boolean(
    content.badgeCelebrationCopy ?? content.badgeSocialShare ?? content.badgeBackgroundTwo
  )
}

/** Replace `{key}` placeholders in CMS copy strings. */
export function formatGamificationMessage(
  message = '',
  values: Record<string, string | number | null | undefined> = {}
) {
  return message.replace(/{[^}]+}/g, (match) => {
    const key = match.slice(1, -1)
    return key in values && values[key] != null ? String(values[key]) : match
  })
}

export function getBadgeDateFormatted(date?: string | null) {
  if (!date) return null
  const parsed = dayjs(date)
  if (!parsed.isValid()) return null
  return parsed.format('M/D/YY')
}

export function getNextMilestone(
  badge: GamificationApi.Badge
): GamificationApi.Badge | null | undefined {
  if (!badge || !isTypeAggregate(badge.type) || isStatusAvailable(badge.status)) {
    return null
  }
  return badge.milestones?.find((milestone) => !isStatusCompleted(milestone.status))
}

export function getAggregateCount(
  count: number | undefined,
  status: GamificationApi.BadgeStatus,
  nextMilestone?: GamificationApi.Badge | null
) {
  if (isStatusActive(status)) {
    return count ?? 0
  }
  return nextMilestone?.count ?? count ?? 0
}

export function getStepPercentage(step: number, totalSteps: number): number {
  if (totalSteps === 0) {
    return 0
  }
  if (step > totalSteps) {
    return 100
  }
  return Math.floor((step * 100) / totalSteps)
}

function getAggregateSpecialText({
  content,
  isActive,
}: {
  content: { aggregateActiveSpecialText?: string }
  isActive: boolean
}) {
  return isActive ? (content.aggregateActiveSpecialText ?? null) : null
}

export function getBadgeCopy(
  badge: GamificationApi.Badge,
  nextMilestone?: GamificationApi.Badge | null,
  hasNextContent = false,
  aggregateActiveContent: {
    aggregateActiveSubtitle?: string
    aggregateActiveSpecialText?: string
  } = {}
): BadgeCopy {
  const { content = {}, status, type, completedDate, currentCount = 0, count = 0 } = badge
  const isActive = isStatusActive(status)
  const isCompleted = isStatusCompleted(status)
  const completedCount = isCompleted && !nextMilestone ? count : currentCount

  if (isTypeAggregate(type) || isTypeTrack(type)) {
    const aggregateCount = getAggregateCount(count, status, nextMilestone)
    const specialText = getAggregateSpecialText({
      content: {
        aggregateActiveSpecialText: aggregateActiveContent.aggregateActiveSpecialText,
      },
      isActive,
    })
    const subtitle = isActive
      ? (aggregateActiveContent.aggregateActiveSubtitle ?? content.subHeadline ?? null)
      : (content.subHeadline ?? null)

    return {
      count: aggregateCount,
      currentCount: completedCount,
      ctaText: null,
      hasProgressBar: isTypeAggregate(type)
        ? isActive || (isCompleted && !!nextMilestone)
        : isActive,
      hideList: isActive,
      messageText: isCompleted
        ? (content.badgeCelebrationCopy ?? null)
        : content.description
          ? formatGamificationMessage(content.description, {
              currentCount: completedCount,
              count: aggregateCount,
            })
          : null,
      hasNextBadge: isCompleted && !!nextMilestone,
      specialText: specialText ?? null,
      subtitle,
    }
  }

  if (isTypeCurated(type)) {
    const ctaText = hasNextContent && !isCompleted ? (content.ctaText ?? null) : null
    const messageText = isCompleted
      ? (content.badgeCelebrationCopy ?? null)
      : content.description
        ? formatGamificationMessage(content.description, { currentCount, count })
        : null
    const specialText = isCompleted
      ? formatGamificationMessage(content.badgeEarnedDateText ?? '', {
          completedDate: getBadgeDateFormatted(completedDate),
        })
      : null
    const subtitle = isCompleted
      ? (content.badgeCelebrationHeadline ?? null)
      : (content.subHeadline ?? null)

    return {
      count,
      currentCount,
      ctaText,
      hasProgressBar: !isCompleted,
      hideList: isCompleted,
      messageText: messageText ?? null,
      hasNextBadge: false,
      specialText: specialText ?? null,
      subtitle,
    }
  }

  return {
    count,
    currentCount,
    ctaText: null,
    hasProgressBar: false,
    hideList: false,
    messageText: content.description
      ? formatGamificationMessage(content.description, { currentCount, count })
      : null,
    hasNextBadge: false,
    specialText: null,
    subtitle: content.subHeadline ?? null,
  }
}
